import { useEffect, useMemo, useRef, useState } from 'react';
import { StickyHeader } from '@generated/components/StickyHeader';
import { HeroCard } from '@generated/components/HeroCard';
import { CompanyCard } from '@generated/components/CompanyCard';
import { MaterialsCard } from '@generated/components/MaterialsCard';
import { LogisticsCard } from '@generated/components/LogisticsCard';
import { SummaryPanel } from '@generated/components/SummaryPanel';
import { ApproveSignCard } from '@generated/components/ApproveSignCard';
import { ConfirmationView, FREQUENCY_LABEL, TRANSPORT_LABEL, COMMITMENT_LABEL } from '@generated/components/ConfirmationView';
import { getMaterials, getRateCardConfig } from '@generated/server/catalog';
import { validateLaunchConfig } from '@generated/server/launch-config';
import { submitReservation, uploadMaterialSpecFile, finalizeReservation } from '@generated/server/submission';
import { commitmentMonths, formatCurrencyWhole, formatShortDate } from '@generated/utils/format';
import { buildSpecFileName } from '@generated/utils/files';
import { formatWeight } from '@generated/utils/units';
import {
  VALID_COMMITMENTS_BY_FREQUENCY,
  annualCostLabel,
  computeAnnualCost,
  computeAnnualUnits,
  computeFillWeightLbs,
  computePeriodCost,
  computeUnitsPerShipment,
  periodCostLabel,
  pluralizeCount,
  shipmentsPerYear,
} from '@generated/utils/pricing';
import { computeValidation } from '@generated/utils/validation';
import { generateReservationPdfBlob, type ReservationPdfSnapshot } from '@generated/utils/reservation-pdf';
import type {
  CompanyInfo,
  Commitment,
  FormatItem,
  Frequency,
  GlobalConfig,
  MaterialItem,
  MaterialSelection,
  Transportation,
  Unit,
} from '@generated/types';

const EMPTY_COMPANY: CompanyInfo = { company: '', contactName: '', email: '', phone: '', shippingOrigin: '' };

export default function App() {
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [formats, setFormats] = useState<FormatItem[]>([]);
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [company, setCompany] = useState<CompanyInfo>(EMPTY_COMPANY);
  const [unit, setUnit] = useState<Unit>('lbs');
  const [selections, setSelections] = useState<Record<string, MaterialSelection>>({});
  const [filesByMaterial, setFilesByMaterial] = useState<Record<string, File[]>>({});
  const [notes, setNotes] = useState('');
  const [formatId, setFormatId] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<Frequency | null>('Monthly');
  const [transportation, setTransportation] = useState<Transportation | null>('Partner arranges');
  const [desiredShipmentDate, setDesiredShipmentDate] = useState<Date | undefined>(undefined);
  const [commitment, setCommitment] = useState<Commitment | null>('12 months');
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('');
  const [signatureDate, setSignatureDate] = useState<Date | null>(new Date());
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Identity for this submission attempt: created once on first Submit press, reused across
  // every retry so a resubmit resumes the same board item instead of creating a duplicate.
  const [submissionKey, setSubmissionKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitStageError, setSubmitStageError] = useState<string | null>(null);
  const [rateChangedNotice, setRateChangedNotice] = useState<string | null>(null);
  const [submissionSaved, setSubmissionSaved] = useState<{ itemId: string; referenceNumber: string } | null>(null);

  // Only set once the FULL pipeline (staged write, files, canonical PDF, finalize commit) has
  // succeeded — this is what actually gates showing the confirmation screen.
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);
  const [costBasisNote, setCostBasisNote] = useState<string | null>(null);
  const [shipmentSizeSentence, setShipmentSizeSentence] = useState<string | null>(null);
  const [finalPdfBlob, setFinalPdfBlob] = useState<Blob | null>(null);
  const [finalPdfFileName, setFinalPdfFileName] = useState<string | null>(null);

  // Re-entrancy guard independent of React's render timing — checked synchronously at the very
  // top of handleSubmit so two rapid clicks (or a click that lands before the disabled attribute
  // has actually committed to the DOM) can never both pass through and create two board items.
  const submittingRef = useRef(false);

  // Load the catalog + rate card configuration ONCE on mount. Every recalculation after that
  // (unit toggle, weights, format/frequency/commitment changes) runs locally against this cached
  // config — no repeated monday queries on ordinary form input.
  const UNAVAILABLE_MESSAGE = 'Reservations are temporarily unavailable. Please try again shortly or contact operations@byfusion.com.';

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    // Launch gate: configuration must prove valid BEFORE the catalog is trusted for any
    // calculation or submission. Any missing/duplicated/invalid Material Catalog, Rate Card,
    // Global setting, or unreachable board fails the whole app closed — no broken numbers, no
    // submit path ever rendered.
    //
    // Cold starts occasionally hit a transient failure in the platform's own internal auth relay
    // (surfaces as a VIBE_AUTH_ERROR/UNAUTHENTICATED before our handler code even runs) that
    // clears itself a moment later. Retry once after a short delay before treating the catalog as
    // genuinely unreachable, so a one-off blip doesn't strand a partner on the unavailable screen.
    const loadCatalog = () =>
      Promise.all([validateLaunchConfig({ data: {} }), getMaterials({ data: {} }), getRateCardConfig({ data: {} })]);

    loadCatalog()
      .catch((err) => {
        console.error('Initial catalog load failed, retrying once', err);
        return new Promise((resolve) => setTimeout(resolve, 1200)).then(loadCatalog);
      })
      .then(([gate, m, rc]) => {
        if (!active) return;
        if (!gate?.ok) {
          console.error('Launch config gate failed', gate?.reasons);
          setCatalogError(UNAVAILABLE_MESSAGE);
          return;
        }
        const loadedMaterials = m?.items ?? [];
        const loadedFormats = rc?.formats ?? [];
        if (loadedMaterials.length === 0 || loadedFormats.length === 0 || !rc?.global) {
          setCatalogError(UNAVAILABLE_MESSAGE);
          return;
        }
        setMaterials(loadedMaterials);
        setFormats(loadedFormats);
        setGlobalConfig(rc.global);
        setFormatId(loadedFormats[0].id);
      })
      .catch((e: Error) => {
        console.error(e);
        if (active) setCatalogError(UNAVAILABLE_MESSAGE);
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  function handleToggleMaterial(id: string, checked: boolean) {
    setSelections((prev) => ({ ...prev, [id]: { checked, weightLbs: checked ? (prev[id]?.weightLbs ?? null) : null } }));
    if (!checked) {
      setFilesByMaterial((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  function handleWeightChange(id: string, lbs: number | null) {
    setSelections((prev) => ({ ...prev, [id]: { checked: prev[id]?.checked ?? true, weightLbs: lbs } }));
  }

  function handleFilesAdd(id: string, newFiles: File[]) {
    setFilesByMaterial((prev) => ({ ...prev, [id]: [...(prev[id] ?? []), ...newFiles] }));
  }

  function handleFileRemove(id: string, idx: number) {
    setFilesByMaterial((prev) => {
      const list = prev[id] ?? [];
      return { ...prev, [id]: list.filter((_, i) => i !== idx) };
    });
  }

  const totalLbs = useMemo(
    () =>
      materials.reduce((sum, m) => {
        const sel = selections[m.id];
        return sel?.checked && sel.weightLbs ? sum + sel.weightLbs : sum;
      }, 0),
    [materials, selections],
  );

  const selectedFormat = formats.find((f) => f.id === formatId);
  const collectionBoxFormat = formats.find((f) => f.name === 'Collection Boxes');
  const gaylordsFormat = formats.find((f) => f.name === 'Gaylords');

  const boxOverCeiling = globalConfig?.boxMaxMonthlyLbs != null && totalLbs > globalConfig.boxMaxMonthlyLbs;
  const disabledFormatIds = useMemo(() => {
    const ids = new Set<string>();
    if (boxOverCeiling && collectionBoxFormat) ids.add(collectionBoxFormat.id);
    return ids;
  }, [boxOverCeiling, collectionBoxFormat]);

  // If the current pick is Collection Boxes and volume crosses the ceiling, redirect to Gaylords.
  useEffect(() => {
    if (boxOverCeiling && collectionBoxFormat && formatId === collectionBoxFormat.id && gaylordsFormat) {
      setFormatId(gaylordsFormat.id);
    }
  }, [boxOverCeiling, collectionBoxFormat, gaylordsFormat, formatId]);

  const validCommitments = frequency ? VALID_COMMITMENTS_BY_FREQUENCY[frequency] : [];
  // If frequency changes and the current term is no longer a valid combination, clear it instead
  // of silently keeping a contradictory pairing.
  useEffect(() => {
    if (frequency && commitment && !VALID_COMMITMENTS_BY_FREQUENCY[frequency].includes(commitment)) {
      setCommitment(null);
    }
  }, [frequency, commitment]);

  const termMonths = commitment ? commitmentMonths(commitment) : 1;
  // totalLbs is the amount for ONE shipment cycle (whatever the chosen frequency's cycle is) —
  // annualize it by the number of shipments per year (12 for Monthly, 4 for Quarterly, 1 for
  // One-Time/Annually), then scale that annual run-rate down to the selected term length.
  const annualLbs = totalLbs * shipmentsPerYear(frequency ?? 'Monthly');
  const termLbs = (annualLbs * termMonths) / 12;

  const fillWeightLbs = useMemo(
    () => computeFillWeightLbs(selectedFormat, materials, selections, globalConfig),
    [selectedFormat, materials, selections, globalConfig],
  );
  const unitsPerShipment = useMemo(
    () => computeUnitsPerShipment(totalLbs, fillWeightLbs),
    [totalLbs, fillWeightLbs],
  );
  const annualUnits = useMemo(
    () => (frequency ? computeAnnualUnits(unitsPerShipment, frequency) : 0),
    [unitsPerShipment, frequency],
  );
  const annualCost = useMemo(
    () => computeAnnualCost(selectedFormat, annualUnits, annualLbs),
    [selectedFormat, annualUnits, annualLbs],
  );
  const periodCost = useMemo(
    () => (frequency ? computePeriodCost(annualCost, frequency) : null),
    [annualCost, frequency],
  );

  const validation = useMemo(
    () =>
      computeValidation({
        company,
        desiredShipmentDate,
        signatoryName,
        signatoryTitle,
        signatureDate,
        authorizationConfirmed,
        materials,
        selections,
        filesByMaterial,
      }),
    [company, desiredShipmentDate, signatoryName, signatoryTitle, signatureDate, authorizationConfirmed, materials, selections, filesByMaterial],
  );

  async function handleSubmit() {
    setSubmitAttempted(true);
    if (!validation.isValid) return;
    if (!formatId || !selectedFormat || !frequency || !transportation || !commitment || !desiredShipmentDate) return;
    // Hard stop: a submission already in flight, already saved, or already fully committed can
    // never start a second one — regardless of whether the disabled button prop has re-rendered yet.
    if (submittingRef.current || submitting || submissionSaved || (submittedAt && referenceNumber)) return;
    submittingRef.current = true;

    // Created once on the first press, reused for every retry in this attempt.
    const key = submissionKey ?? crypto.randomUUID();
    if (!submissionKey) setSubmissionKey(key);

    // Trim every text field and lowercase the email once, at the submission boundary — this is
    // what actually gets sent to the server, put into the canonical PDF, and shown on the
    // confirmation screen, so all three can never disagree on casing/whitespace.
    const normalizedCompany: CompanyInfo = {
      company: company.company.trim(),
      contactName: company.contactName.trim(),
      email: company.email.trim().toLowerCase(),
      phone: company.phone.trim(),
      shippingOrigin: company.shippingOrigin.trim(),
    };
    const normalizedSignatoryName = signatoryName.trim();
    const normalizedSignatoryTitle = signatoryTitle.trim();
    const normalizedNotes = notes.trim();
    setCompany(normalizedCompany);
    setSignatoryName(normalizedSignatoryName);
    setSignatoryTitle(normalizedSignatoryTitle);
    setNotes(normalizedNotes);

    setSubmitting(true);
    setSubmitStageError(null);
    setRateChangedNotice(null);

    try {
      const tickedMaterials = materials.filter((m) => selections[m.id]?.checked);
      const materialsPayload = tickedMaterials.map((m) => ({
        id: m.id,
        name: m.name,
        mapsTo: m.mapsTo ?? null,
        weightLbs: selections[m.id]?.weightLbs ?? 0,
        requiresUpload: m.requiresUpload,
        fileNames: (filesByMaterial[m.id] ?? []).map((f) => buildSpecFileName(m.name, f.name)),
      }));

      const cachedFormat = {
        nominalFillLbs: selectedFormat.nominalFillLbs,
        volumetric: selectedFormat.volumetric,
        palletRequired: selectedFormat.palletRequired,
        handlingPerUnit: selectedFormat.handlingPerUnit,
        pricePerTon: selectedFormat.pricePerTon,
        ratesEffective: selectedFormat.ratesEffective ? selectedFormat.ratesEffective.toISOString() : null,
      };
      const cachedGlobal = {
        boxMaxMonthlyLbs: globalConfig?.boxMaxMonthlyLbs ?? null,
        volumetricFillMinLbs: globalConfig?.volumetricFillMinLbs ?? null,
        volumetricFillMaxLbs: globalConfig?.volumetricFillMaxLbs ?? null,
      };

      const res = await submitReservation({
        data: {
          submissionKey: key,
          company: normalizedCompany,
          unit,
          materials: materialsPayload,
          totalLbs,
          termLbs,
          formatId,
          formatName: selectedFormat.name,
          frequency,
          transportation,
          commitment,
          desiredShipmentDate: desiredShipmentDate.toISOString(),
          unitsPerShipment,
          fillWeightLbs,
          periodCost,
          annualCost,
          signatoryName: normalizedSignatoryName,
          signatoryTitle: normalizedSignatoryTitle,
          signatureDate: (signatureDate ?? new Date()).toISOString(),
          notes: normalizedNotes,
          cachedFormat,
          cachedGlobal,
        },
      });

      if (res.status === 'rate_changed') {
        setGlobalConfig(res.global);
        setFormats(res.formats);
        setAuthorizationConfirmed(false);
        setRateChangedNotice(
          "Rates changed while this form was open — we've updated your estimate below. Please review the new figures, re-check the authorization box, and submit again.",
        );
        return;
      }

      // Files append on monday's file columns, so each upload goes separately, sequentially,
      // keeping the material association via the sanitized filename prefix.
      for (const m of tickedMaterials) {
        const files = filesByMaterial[m.id] ?? [];
        for (const file of files) {
          const renamed = new File([file], buildSpecFileName(m.name, file.name), { type: file.type });
          const form = new FormData();
          form.append('file', renamed);
          form.append('itemId', res.itemId);
          form.append('submissionKey', key);
          await uploadMaterialSpecFile({ data: form });
        }
      }

      setSubmissionSaved({ itemId: res.itemId, referenceNumber: res.referenceNumber });

      // Stage 2 — build the canonical, countersignable PDF from this frozen snapshot (not from a
      // later live re-read of the board) and finalize: recheck the reference, upload the PDF,
      // verify everything required is present, and only then flip Status to "New Request". Whether
      // a material spec upload is actually required is re-derived by the server from the Material
      // Catalog board itself — not from anything the client sends — so this count is display-only.
      const isHandling = selectedFormat.handlingPerUnit != null;
      const noun = pluralizeCount(unitsPerShipment, selectedFormat.name);
      const shipmentSizeSentence = selectedFormat.volumetric
        ? `${unitsPerShipment} ${noun} per shipment (~${Math.round(fillWeightLbs)} lbs each)`
        : `${unitsPerShipment} ${noun} per shipment`;

      const snapshot: ReservationPdfSnapshot = {
        referenceNumber: res.referenceNumber,
        preparedDate: new Date(),
        ratesEffectiveDate: res.ratesEffective ? new Date(res.ratesEffective) : null,
        companyName: normalizedCompany.company,
        contactName: normalizedCompany.contactName,
        email: normalizedCompany.email,
        phone: normalizedCompany.phone,
        shippingOrigin: normalizedCompany.shippingOrigin,
        formatName: selectedFormat.name,
        frequencyLabel: frequency ? FREQUENCY_LABEL[frequency] : '',
        commitmentLabel: commitment ? COMMITMENT_LABEL[commitment] : '',
        unit,
        materialLines: tickedMaterials.map((m) => ({
          name: m.name,
          displayWeight: formatWeight(selections[m.id]?.weightLbs ?? 0, unit),
        })),
        totalWeightLbsLabel: formatWeight(totalLbs, 'lbs'),
        transportationLabel: transportation ? TRANSPORT_LABEL[transportation] : '',
        desiredShipmentDateLabel: formatShortDate(desiredShipmentDate),
        shipmentSizeSentence,
        periodCostLabel: periodCostLabel(frequency, isHandling),
        periodCostValue: periodCost != null ? formatCurrencyWhole(periodCost) : null,
        annualCostLabel: annualCostLabel(isHandling),
        annualCostValue: annualCost != null ? formatCurrencyWhole(annualCost) : null,
        costBasisNote: res.costBasisNote,
        signatoryName: normalizedSignatoryName,
        signatoryTitle: normalizedSignatoryTitle,
        signatureDateLabel: formatShortDate(signatureDate ?? new Date()),
      };

      const pdfBlob = await generateReservationPdfBlob(snapshot);
      const pdfName = `${res.referenceNumber} ByFusion capacity reservation.pdf`;

      const finalizeForm = new FormData();
      finalizeForm.append('itemId', res.itemId);
      finalizeForm.append('referenceNumber', res.referenceNumber);
      finalizeForm.append('pdf', new File([pdfBlob], pdfName, { type: 'application/pdf' }));
      finalizeForm.append('submissionKey', key);

      await finalizeReservation({ data: finalizeForm });

      // Status is now "New Request" on the board — automations may fire. The confirmation screen's
      // Download button lets the partner save the canonical PDF on demand; no auto-download here.
      setCostBasisNote(res.costBasisNote);
      setShipmentSizeSentence(shipmentSizeSentence);
      setFinalPdfBlob(pdfBlob);
      setFinalPdfFileName(pdfName);
      setSubmittedAt(new Date());
      setReferenceNumber(res.referenceNumber);
      window.scrollTo({ top: 0 });
    } catch (e) {
      console.error(e);
      setSubmissionSaved(null);
      setSubmitStageError("Couldn't complete the submission. Try again, or contact operations@byfusion.com.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function handleReset() {
    submittingRef.current = false;
    setCompany(EMPTY_COMPANY);
    setUnit('lbs');
    setSelections({});
    setFilesByMaterial({});
    setNotes('');
    setFormatId(formats[0]?.id ?? null);
    setFrequency('Monthly');
    setTransportation('Partner arranges');
    setDesiredShipmentDate(undefined);
    setCommitment('12 months');
    setSignatoryName('');
    setSignatoryTitle('');
    setSignatureDate(new Date());
    setAuthorizationConfirmed(false);
    setSubmitAttempted(false);
    setSubmissionKey(null);
    setSubmitting(false);
    setSubmitStageError(null);
    setRateChangedNotice(null);
    setSubmissionSaved(null);
    setReferenceNumber(null);
    setSubmittedAt(null);
    setCostBasisNote(null);
    setShipmentSizeSentence(null);
    setFinalPdfBlob(null);
    setFinalPdfFileName(null);
    window.scrollTo({ top: 0 });
  }

  if (submittedAt && referenceNumber) {
    return (
      <div className="min-h-screen bg-background">
        <div className="print:hidden">
          <StickyHeader referenceNumber={referenceNumber} />
        </div>
        <ConfirmationView
          referenceNumber={referenceNumber}
          submittedAt={submittedAt}
          company={company}
          materials={materials}
          selections={selections}
          unit={unit}
          totalLbs={totalLbs}
          formats={formats}
          formatId={formatId}
          frequency={frequency}
          transportation={transportation}
          desiredShipmentDate={desiredShipmentDate}
          commitment={commitment}
          shipmentSizeSentence={shipmentSizeSentence}
          periodCost={periodCost}
          annualCost={annualCost}
          costBasisNote={costBasisNote}
          signatoryName={signatoryName}
          signatoryTitle={signatoryTitle}
          signatureDate={signatureDate}
          pdfBlob={finalPdfBlob}
          pdfFileName={finalPdfFileName}
          onReset={handleReset}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StickyHeader referenceNumber={null} />
      <main className="mx-auto max-w-[900px] px-4 pb-20 sm:px-6">
        <HeroCard />

        {catalogError ? (
          <div className="mt-5 rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            {catalogError}
          </div>
        ) : (
          <>
            <CompanyCard
              company={company}
              onChange={(patch) => setCompany((prev) => ({ ...prev, ...patch }))}
              showErrors={submitAttempted}
              companyInvalid={validation.companyInvalid}
              contactNameInvalid={validation.contactNameInvalid}
              emailInvalid={validation.emailInvalid}
              shippingOriginInvalid={validation.shippingOriginInvalid}
            />

            <MaterialsCard
              materials={materials}
              loading={catalogLoading}
              unit={unit}
              onUnitChange={setUnit}
              selections={selections}
              onToggleMaterial={handleToggleMaterial}
              onWeightChange={handleWeightChange}
              files={filesByMaterial}
              onFilesAdd={handleFilesAdd}
              onFileRemove={handleFileRemove}
              notes={notes}
              onNotesChange={setNotes}
              showErrors={submitAttempted}
              weightErrorIds={validation.materialWeightErrorIds}
              uploadErrorIds={validation.materialUploadErrorIds}
              frequency={frequency}
              onFrequencyChange={setFrequency}
            />

            <LogisticsCard
              formats={formats}
              loading={catalogLoading}
              formatId={formatId}
              onFormatChange={setFormatId}
              disabledFormatIds={disabledFormatIds}
              boxCeilingGlobal={globalConfig}
              collectionBoxFormat={collectionBoxFormat}
              frequency={frequency}
              transportation={transportation}
              onTransportationChange={setTransportation}
              desiredShipmentDate={desiredShipmentDate}
              onDateChange={setDesiredShipmentDate}
              commitment={commitment}
              onCommitmentChange={setCommitment}
              validCommitments={validCommitments}
              monthlyWeightLbs={totalLbs}
              unit={unit}
              unitsPerShipment={unitsPerShipment}
              fillWeightLbs={fillWeightLbs}
              showErrors={submitAttempted}
              desiredShipmentDateInvalid={validation.desiredShipmentDateInvalid}
            />

            <SummaryPanel
              monthlyWeightLbs={totalLbs}
              termWeightLbs={termLbs}
              unit={unit}
              frequency={frequency}
              commitment={commitment}
              periodCost={periodCost}
              annualCost={annualCost}
              selectedFormat={selectedFormat}
            />

            <ApproveSignCard
              signatoryName={signatoryName}
              onSignatoryNameChange={setSignatoryName}
              signatoryTitle={signatoryTitle}
              onSignatoryTitleChange={setSignatoryTitle}
              signatureDate={signatureDate}
              onSignatureDateChange={(d) => setSignatureDate(d ?? null)}
              authorizationConfirmed={authorizationConfirmed}
              onAuthorizationChange={setAuthorizationConfirmed}
              showErrors={submitAttempted}
              signatoryNameInvalid={validation.signatoryNameInvalid}
              signatoryTitleInvalid={validation.signatoryTitleInvalid}
              signatureDateInvalid={validation.signatureDateInvalid}
              authorizationInvalid={validation.authorizationInvalid}
              summaryMessage={validation.summaryMessage}
              onSubmit={handleSubmit}
              onReset={handleReset}
              submitting={submitting}
              submitStageError={submitStageError}
              rateChangedNotice={rateChangedNotice}
              submissionSaved={submissionSaved}
            />

            <p className="mt-10 text-center text-xs text-muted-foreground">
              ByFusion Global, Inc. · operations@byfusion.com · +1 833 BYBLOCK · byfusion.com
            </p>
          </>
        )}
      </main>
    </div>
  );
}
